import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Grid,
  Button,
  Checkbox,
  Typography,
  Pagination,
  Card,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import {
  startOfISOWeek,
  endOfISOWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
  format,
  startOfDay,
  endOfDay,
  parseISO,
} from "date-fns";
import { apiGetPostRewards } from "../../../services/apiGetPostRewards";
import { apiBulkUpdateVoucherSent } from "../../../services/apiBulkUpdateVoucherSent";
import { Loader } from "../../../components/ui/Loader";

const STATUS_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "true", label: "Voucher Enviado" },
  { value: "false", label: "Pendente" },
];

const APPROVED_POSTS_COUNT_OPTIONS = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: "3+", label: "3+" },
];

export const PostRewardsReport = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 1,
  });

  // Date filters
  const [startDate, setStartDate] = useState(() => {
    // Default to current week's Monday
    return format(startOfISOWeek(new Date()), "yyyy-MM-dd");
  });
  const [endDate, setEndDate] = useState(() => {
    // Default to current week's Sunday
    return format(endOfISOWeek(new Date()), "yyyy-MM-dd");
  });
  // Voucher status filter
  const [voucherStatusFilter, setVoucherStatusFilter] = useState("all");
  // Approved posts count filter: null | 1 | 2 | "3+" (mutually exclusive)
  const [approvedPostsCountFilter, setApprovedPostsCountFilter] = useState(null);
  const [activePreset, setActivePreset] = useState("thisWeek");
  const [exporting, setExporting] = useState(false);

  const buildFilterParams = () => {
    const params = {};

    // parseISO interprets "YYYY-MM-DD" as LOCAL midnight, whereas
    // `new Date("YYYY-MM-DD")` would parse it as UTC midnight and shift the
    // resulting day by the browser offset (e.g. selecting 2026-05-01 in a
    // BRT browser would filter from 2026-04-30 03:00 UTC = 2026-04-30 BRT).
    // toISOString() then converts the local-time boundaries to the UTC
    // instants the backend stores in posted_at.
    if (startDate) {
      params.startDate = startOfDay(parseISO(startDate)).toISOString();
    }

    if (endDate) {
      params.endDate = endOfDay(parseISO(endDate)).toISOString();
    }

    if (voucherStatusFilter !== "all") {
      params.isVoucherSent = voucherStatusFilter === "true";
    }

    if (approvedPostsCountFilter === "3+") {
      params.minApprovedPostsCount = 3;
    } else if (approvedPostsCountFilter !== null) {
      params.approvedPostsCount = approvedPostsCountFilter;
    }

    return params;
  };

  const fetchRewards = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        ...buildFilterParams(),
        page,
        limit: pagination.limit,
      };

      const response = await apiGetPostRewards(params);
      setRewards(response.data || []);
      setPagination(response.pagination || {
        page: 1,
        limit: 20,
        totalCount: 0,
        totalPages: 1,
      });
      setSelectedIds([]);
    } catch (error) {
      console.error("Error fetching post rewards:", error);
      setRewards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRewards(1);
  }, [startDate, endDate, voucherStatusFilter, approvedPostsCountFilter]);

  const handlePageChange = (event, value) => {
    fetchRewards(value);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(rewards.map((r) => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((i) => i !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleBulkUpdate = async (isVoucherSent) => {
    if (selectedIds.length === 0) {
      alert("Selecione ao menos um registro");
      return;
    }

    try {
      setLoading(true);
      await apiBulkUpdateVoucherSent(selectedIds, isVoucherSent);
      await fetchRewards(pagination.page);
      alert(`${selectedIds.length} registro(s) atualizado(s) com sucesso!`);
    } catch (error) {
      console.error("Error updating voucher status:", error);
      alert("Erro ao atualizar status dos vouchers");
    } finally {
      setLoading(false);
    }
  };

  const handleDatePreset = (preset) => {
    const today = new Date();
    setActivePreset(preset);
    switch (preset) {
      case "thisWeek":
        setStartDate(format(startOfISOWeek(today), "yyyy-MM-dd"));
        setEndDate(format(endOfISOWeek(today), "yyyy-MM-dd"));
        break;
      case "lastWeek":
        setStartDate(format(startOfISOWeek(subWeeks(today, 1)), "yyyy-MM-dd"));
        setEndDate(format(endOfISOWeek(subWeeks(today, 1)), "yyyy-MM-dd"));
        break;
      case "thisMonth":
        setStartDate(format(startOfMonth(today), "yyyy-MM-dd"));
        setEndDate(format(endOfMonth(today), "yyyy-MM-dd"));
        break;
      case "lastMonth":
        setStartDate(format(startOfMonth(subMonths(today, 1)), "yyyy-MM-dd"));
        setEndDate(format(endOfMonth(subMonths(today, 1)), "yyyy-MM-dd"));
        break;
      default:
        break;
    }
  };

  const handleManualDateChange = (type, value) => {
    setActivePreset(null);
    if (type === "start") {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  };

  const handleApprovedPostsCountFilterClick = (value) => {
    setApprovedPostsCountFilter((prev) => (prev === value ? null : value));
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const filterParams = buildFilterParams();
      const exportLimit = 100;

      const firstResponse = await apiGetPostRewards({
        ...filterParams,
        page: 1,
        limit: exportLimit,
      });
      let allRewards = firstResponse.data || [];
      const totalPages = firstResponse.pagination?.totalPages || 1;

      if (totalPages > 1) {
        const remainingPages = Array.from(
          { length: totalPages - 1 },
          (_, i) => i + 2
        );
        const remainingResponses = await Promise.all(
          remainingPages.map((page) =>
            apiGetPostRewards({ ...filterParams, page, limit: exportLimit })
          )
        );
        remainingResponses.forEach((response) => {
          allRewards = allRewards.concat(response.data || []);
        });
      }

      exportToExcel(allRewards);
    } catch (error) {
      console.error("Error exporting post rewards:", error);
      alert("Erro ao exportar relatório");
    } finally {
      setExporting(false);
    }
  };

  if (loading && rewards.length === 0) {
    return <Loader />;
  }

  const allSelected = rewards.length > 0 && selectedIds.length === rewards.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < rewards.length;

  return (
    <Box>
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
          Filtros
        </Typography>

        {/* Date Presets */}
        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant={activePreset === "thisWeek" ? "contained" : "outlined"}
            onClick={() => handleDatePreset("thisWeek")}
          >
            Esta Semana
          </Button>
          <Button
            size="small"
            variant={activePreset === "lastWeek" ? "contained" : "outlined"}
            onClick={() => handleDatePreset("lastWeek")}
          >
            Semana Passada
          </Button>
          <Button
            size="small"
            variant={activePreset === "thisMonth" ? "contained" : "outlined"}
            onClick={() => handleDatePreset("thisMonth")}
          >
            Este Mês
          </Button>
          <Button
            size="small"
            variant={activePreset === "lastMonth" ? "contained" : "outlined"}
            onClick={() => handleDatePreset("lastMonth")}
          >
            Mês Passado
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Data Início"
              type="date"
              value={startDate}
              onChange={(e) => handleManualDateChange("start", e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Data Fim"
              type="date"
              value={endDate}
              onChange={(e) => handleManualDateChange("end", e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status do Voucher</InputLabel>
              <Select
                value={voucherStatusFilter}
                label="Status do Voucher"
                onChange={(e) => setVoucherStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Posts Aprovados no Mês
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {APPROVED_POSTS_COUNT_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  size="small"
                  variant={approvedPostsCountFilter === option.value ? "contained" : "outlined"}
                  onClick={() => handleApprovedPostsCountFilterClick(option.value)}
                >
                  {option.label}
                </Button>
              ))}
              <Button
                size="small"
                color="inherit"
                disabled={approvedPostsCountFilter === null}
                onClick={() => setApprovedPostsCountFilter(null)}
              >
                Limpar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <Card sx={{ p: 2, mb: 3, backgroundColor: "#f5f5f5" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              {selectedIds.length} selecionado(s)
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleBulkUpdate(true)}
              disabled={loading}
            >
              Marcar como Enviado
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleBulkUpdate(false)}
              disabled={loading}
            >
              Marcar como Pendente
            </Button>
          </Box>
        </Card>
      )}

      {/* Results Summary and Export */}
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Mostrando {rewards.length} de {pagination.totalCount} resultado(s)
        </Typography>
        <Tooltip title="Exportar todos os resultados filtrados para Excel">
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={loading || exporting || pagination.totalCount === 0}
            sx={{
              color: "var(--primary-color)",
              borderColor: "var(--primary-color)",
              "&:hover": {
                borderColor: "var(--primary-color-dark)",
                backgroundColor: "rgba(var(--primary-color-rgb), 0.04)",
                transform: "scale(1.02)",
                transition: "all 0.2s",
              },
            }}
          >
            {exporting ? "Exportando..." : "Exportar"}
          </Button>
        </Tooltip>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Creator</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Unidade</TableCell>
              <TableCell>Post ID</TableCell>
              <TableCell>Data do Primeiro Post</TableCell>
              <TableCell>Posts Aprovados no Mês</TableCell>
              <TableCell>Voucher Enviado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rewards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Nenhum registro encontrado
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rewards.map((reward) => (
                <TableRow
                  key={reward.id}
                  hover
                  sx={{
                    cursor: "pointer",
                    backgroundColor: selectedIds.includes(reward.id)
                      ? "rgba(25, 118, 210, 0.08)"
                      : "inherit",
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(reward.id)}
                      onChange={() => handleSelectOne(reward.id)}
                    />
                  </TableCell>
                  <TableCell>{reward.id}</TableCell>
                  <TableCell>{reward.creator?.name || "-"}</TableCell>
                  <TableCell>{reward.creator?.email || "-"}</TableCell>
                  <TableCell>
                    {reward.creator?.franchise?.name || "-"}
                  </TableCell>
                  <TableCell>{reward.postId}</TableCell>
                  <TableCell>
                    {format(new Date(reward.postedAt), "dd/MM/yyyy HH:mm")}
                  </TableCell>
                  <TableCell>{reward.approvedPostsCount}</TableCell>
                  <TableCell>
                    <Chip
                      label={reward.isVoucherSent ? "Enviado" : "Pendente"}
                      size="small"
                      color={reward.isVoucherSent ? "success" : "warning"}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            disabled={loading}
          />
        </Box>
      )}
    </Box>
  );
};

const exportToExcel = (rewards) => {
  const headers = [
    "ID",
    "Creator",
    "Email",
    "Unidade",
    "Post ID",
    "Data do Primeiro Post",
    "Posts Aprovados no Mês",
    "Voucher Enviado",
  ];

  const data = rewards.map((reward) => [
    reward.id,
    reward.creator?.name || "-",
    reward.creator?.email || "-",
    reward.creator?.franchise?.name || "-",
    reward.postId,
    format(new Date(reward.postedAt), "dd/MM/yyyy HH:mm"),
    reward.approvedPostsCount,
    reward.isVoucherSent ? "Enviado" : "Pendente",
  ]);

  const worksheetData = [headers, ...data];
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Voucher Mensal");
  
  XLSX.writeFile(
    workbook,
    `voucher_mensal_relatorio_${new Date().toISOString().split("T")[0]}.xlsx`
  );
};
